import { useState, useCallback } from 'react';
import {
  makeStyles,
  Box,
  Typography,
  Chip,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  CircularProgress,
  Link,
  Collapse,
  List,
  Divider,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import {
  Description as ProjectIcon,
  OpenInNew as OpenInNewIcon,
  Storage as ResourceIcon,
  AccountTree as SystemIcon,
  MenuBook as DocsIcon,
  Link as LinkIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  VisibilityOff as HideIcon,
  Delete as DeleteIcon,
} from '@material-ui/icons';
import WarningIcon from '@material-ui/icons/Warning';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { Alert } from '@material-ui/lab';
import { HomepageGcpProject } from '../../types';

// GCP Cloud Icon SVG component
const GcpIcon = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    style={{ marginRight: 4 }}
  >
    <path d="M12.19 2.38a9.344 9.344 0 0 0-9.234 6.893c.053-.02-.055.013 0 0-3.875 2.551-3.922 8.11-.247 10.941l.006-.007-.007.03a6.717 6.717 0 0 0 4.077 1.356h5.173l.03.03h5.192c6.687.053 9.376-8.605 3.835-12.35a9.365 9.365 0 0 0-8.825-6.893zm0 1.867a7.47 7.47 0 0 1 7.029 5.476l.217.783.835.174c4.818 1.006 5.934 7.505 1.508 9.398l-.06.028h-4.337l-.03-.03H12.19l-.032.03H7.816a4.85 4.85 0 0 1-2.941-.977l-.023-.018.018-.01c-2.593-1.98-2.556-5.89.317-7.62l.65-.393-.15-.725A7.467 7.467 0 0 1 12.19 4.247z"/>
  </svg>
);

// Backstage Icon SVG component
const BackstageIcon = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    style={{ marginRight: 4 }}
  >
    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
  </svg>
);

const useStyles = makeStyles((theme) => ({
  listItem: {
    paddingLeft: theme.spacing(2),
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  projectRow: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    padding: theme.spacing(1, 0),
  },
  projectMain: {
    display: 'flex',
    alignItems: 'flex-start',
    flex: 1,
    cursor: 'pointer',
  },
  projectContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  projectHeader: {
    display: 'flex',
    alignItems: 'center',
  },
  chip: {
    marginLeft: theme.spacing(1),
    height: 20,
  },
  labelChip: {
    marginLeft: theme.spacing(0.5),
    height: 18,
    fontSize: '0.65rem',
  },
  labelsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  projectActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  actionLink: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    textDecoration: 'none',
    fontSize: '0.75rem',
    fontWeight: 500,
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  consoleLink: {
    color: '#4285F4',
    border: '1px solid #4285F4',
  },
  catalogLink: {
    color: '#36BAA2',
    border: '1px solid #36BAA2',
  },
  catalogLinkInactive: {
    color: '#999',
    border: '1px solid #999',
    opacity: 0.6,
  },
  catalogPanel: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(4),
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
  },
  catalogSection: {
    marginBottom: theme.spacing(2),
  },
  catalogSectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  catalogItem: {
    cursor: 'pointer',
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  catalogItemText: {
    '& .MuiListItemText-primary': {
      fontSize: '0.875rem',
    },
    '& .MuiListItemText-secondary': {
      fontSize: '0.75rem',
    },
  },
  noCatalogMatch: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  loadingIndicator: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  loadingSpinner: {
    marginRight: theme.spacing(1),
  },
  externalLinkChip: {
    margin: theme.spacing(0.5),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  linksContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  techdocsButton: {
    marginTop: theme.spacing(1),
  },
  groupChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  groupChip: {
    height: 20,
    fontSize: '0.7rem',
  },
  deleteButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
  confirmWarning: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  billingSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(0.5),
    flexWrap: 'wrap',
  },
  billingItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.75rem',
  },
  billingLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
  },
  billingValue: {
    fontWeight: 500,
    fontSize: '0.8rem',
  },
  billingHighAlert: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  billingIncrease: {
    color: theme.palette.warning.dark,
  },
  billingDecrease: {
    color: theme.palette.success.dark,
  },
  billingArrow: {
    fontSize: '0.9rem',
    verticalAlign: 'middle',
  },
  billingLink: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.7rem',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

interface EntityLink {
  url: string;
  title?: string;
  icon?: string;
  type?: string;
  source: 'entity' | 'dependency';
  sourceEntityName?: string;
}

interface CatalogEntityInfo {
  entity: Entity;
  entityRef: string;
  parentEntities: Entity[];
  dependencyEntities: Entity[];
  links: EntityLink[];
  techdocsUrl: string | null;
}

interface CatalogState {
  loading: boolean;
  error: string | null;
  data: CatalogEntityInfo | null;
  notFound: boolean;
}

interface CatalogExistsState {
  checked: boolean;
  exists: boolean;
  entityRef: string | null;
}

export interface GcpProjectItemProps {
  project: HomepageGcpProject;
  editMode?: boolean;
  onHide?: (project: HomepageGcpProject) => void;
  onEditGroups?: (project: HomepageGcpProject) => void;
  onDelete?: (project: HomepageGcpProject) => Promise<void>;
  userGroups?: string[];
  isOrgView?: boolean;
}

export const GcpProjectItem = ({
  project,
  editMode = false,
  onHide,
  onEditGroups,
  onDelete,
  userGroups = [],
  isOrgView = false,
}: GcpProjectItemProps) => {
  const classes = useStyles();
  const config = useApi(configApiRef);
  const catalogApi = useApi(catalogApiRef);

  const [expanded, setExpanded] = useState(false);
  const [catalogState, setCatalogState] = useState<CatalogState>({
    loading: false,
    error: null,
    data: null,
    notFound: false,
  });
  const [catalogExists, setCatalogExists] = useState<CatalogExistsState>({
    checked: false,
    exists: false,
    entityRef: null,
  });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const appBaseUrl = config.getString('app.baseUrl');

  // Build catalog URL for a project entity
  const getCatalogEntityUrl = useCallback((entity: Entity) => {
    const namespace = entity.metadata.namespace || 'default';
    const kind = entity.kind.toLowerCase();
    const name = entity.metadata.name;
    return `${appBaseUrl}/catalog/${namespace}/${kind}/${name}`;
  }, [appBaseUrl]);

  // Build TechDocs URL for an entity
  const getTechDocsUrl = useCallback((entity: Entity): string | null => {
    const techdocsRef = entity.metadata.annotations?.['backstage.io/techdocs-ref'];
    if (!techdocsRef) return null;

    const namespace = entity.metadata.namespace || 'default';
    const kind = entity.kind.toLowerCase();
    const name = entity.metadata.name;
    return `${appBaseUrl}/docs/${namespace}/${kind}/${name}`;
  }, [appBaseUrl]);

  // Fetch catalog entity by GCP project ID
  const fetchCatalogEntity = useCallback(async (projectId: string): Promise<Entity | null> => {
    const gcpAnnotationValue = `projects/${projectId}`;

    try {
      const response = await catalogApi.getEntities({
        filter: {
          kind: 'Component',
          'spec.type': 'gcp-project',
          'metadata.annotations.gcp.io/id': gcpAnnotationValue,
        },
      });

      return response.items.length > 0 ? response.items[0] : null;
    } catch (err) {
      console.error(`Error fetching catalog entity for ${projectId}:`, err);
      return null;
    }
  }, [catalogApi]);

  // Fetch parent/ancestor entities
  const fetchParentEntities = useCallback(async (entityRef: string): Promise<Entity[]> => {
    try {
      const ancestors = await catalogApi.getEntityAncestors({ entityRef });
      return ancestors.items
        .map(item => item.entity)
        .filter(e => `${e.kind.toLowerCase()}:${e.metadata.namespace || 'default'}/${e.metadata.name}` !== entityRef);
    } catch (err) {
      console.error(`Error fetching ancestors for ${entityRef}:`, err);
      return [];
    }
  }, [catalogApi]);

  // Fetch dependency entities from the entity's spec.dependsOn
  const fetchDependencyEntities = useCallback(async (entity: Entity): Promise<Entity[]> => {
    const dependsOn = entity.spec?.dependsOn as string[] | string | undefined;
    if (!dependsOn) return [];

    const depRefs = Array.isArray(dependsOn) ? dependsOn : [dependsOn];
    const dependencies = await Promise.all(
      depRefs.map(async (ref) => {
        try {
          let kind = 'component';
          let namespace = 'default';
          let name = ref;

          if (ref.includes(':')) {
            const [kindPart, rest] = ref.split(':');
            kind = kindPart;
            if (rest.includes('/')) {
              [namespace, name] = rest.split('/');
            } else {
              name = rest;
            }
          } else if (ref.includes('/')) {
            [namespace, name] = ref.split('/');
          }

          const result = await catalogApi.getEntityByRef(`${kind}:${namespace}/${name}`);
          return result || null;
        } catch {
          return null;
        }
      })
    );

    return dependencies.filter((d): d is Entity => d !== null);
  }, [catalogApi]);

  // Aggregate links from entity and its dependencies
  const aggregateLinks = useCallback((entity: Entity, dependencies: Entity[]): EntityLink[] => {
    const links: EntityLink[] = [];

    if (entity.metadata.links) {
      links.push(...entity.metadata.links.map(link => ({
        ...link,
        source: 'entity' as const,
      })));
    }

    dependencies.forEach(dep => {
      if (dep.metadata.links) {
        links.push(...dep.metadata.links.map(link => ({
          ...link,
          source: 'dependency' as const,
          sourceEntityName: dep.metadata.name,
        })));
      }
    });

    return links;
  }, []);

  // Check if catalog entity exists
  const checkCatalogExists = useCallback(async () => {
    if (catalogExists.checked) return;

    try {
      const entity = await fetchCatalogEntity(project.projectId);
      setCatalogExists({
        checked: true,
        exists: !!entity,
        entityRef: entity ? `${entity.kind.toLowerCase()}:${entity.metadata.namespace || 'default'}/${entity.metadata.name}` : null,
      });
    } catch {
      setCatalogExists({ checked: true, exists: false, entityRef: null });
    }
  }, [project.projectId, catalogExists.checked, fetchCatalogEntity]);

  // Fetch full catalog data
  const fetchCatalogData = useCallback(async () => {
    if (catalogState.data || catalogState.loading) return;

    setCatalogState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const entity = await fetchCatalogEntity(project.projectId);

      if (!entity) {
        setCatalogState({ loading: false, error: null, data: null, notFound: true });
        return;
      }

      const entityRef = `${entity.kind.toLowerCase()}:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`;
      const [parentEntities, dependencyEntities] = await Promise.all([
        fetchParentEntities(entityRef),
        fetchDependencyEntities(entity),
      ]);

      const links = aggregateLinks(entity, dependencyEntities);
      const techdocsUrl = getTechDocsUrl(entity);

      setCatalogState({
        loading: false,
        error: null,
        data: {
          entity,
          entityRef,
          parentEntities,
          dependencyEntities,
          links,
          techdocsUrl,
        },
        notFound: false,
      });
    } catch (err: any) {
      setCatalogState({
        loading: false,
        error: err.message || 'Failed to load catalog data',
        data: null,
        notFound: false,
      });
    }
  }, [project.projectId, catalogState.data, catalogState.loading, fetchCatalogEntity, fetchParentEntities, fetchDependencyEntities, aggregateLinks, getTechDocsUrl]);

  // Toggle catalog panel
  const toggleExpanded = useCallback(() => {
    const willExpand = !expanded;
    setExpanded(willExpand);
    if (willExpand && !catalogState.data && !catalogState.loading) {
      fetchCatalogData();
    }
  }, [expanded, catalogState.data, catalogState.loading, fetchCatalogData]);

  // Check catalog exists on render
  if (!catalogExists.checked) {
    checkCatalogExists();
  }

  const openGcpConsole = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    window.open(
      `https://console.cloud.google.com/home/dashboard?project=${project.projectId}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const getCatalogUrl = () => {
    if (catalogExists.entityRef) {
      const [kindNs, name] = catalogExists.entityRef.split('/');
      const [kind, namespace] = kindNs.includes(':') ? kindNs.split(':') : ['component', kindNs];
      return `${appBaseUrl}/catalog/${namespace || 'default'}/${kind}/${name}`;
    }
    return `${appBaseUrl}/catalog?filters[kind]=component&filters[metadata.name]=${project.projectId}`;
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setDeleting(true);
    try {
      await onDelete(project);
      setConfirmDeleteOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const formatGroupName = (groupRef: string) => {
    if (groupRef.startsWith('user:')) {
      return 'Personal';
    }
    const parts = groupRef.split('/');
    const name = parts[parts.length - 1];
    return name.charAt(0).toUpperCase() + name.slice(1).replace(/-/g, ' ');
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Render billing info
  const renderBillingInfo = () => {
    const billing = project.billing;
    if (!billing) return null;

    const percentChange = billing.previousMonthCost > 0
      ? ((billing.projectedMonthCost - billing.previousMonthCost) / billing.previousMonthCost) * 100
      : 0;

    const isHighAlert = percentChange > 50;
    const isIncrease = billing.projectedMonthCost > billing.previousMonthCost;
    const isDecrease = billing.projectedMonthCost < billing.previousMonthCost;

    return (
      <Box className={classes.billingSection}>
        <Box className={classes.billingItem}>
          <Typography className={classes.billingLabel}>Prev:</Typography>
          <Typography className={classes.billingValue}>
            {formatCurrency(billing.previousMonthCost, billing.currency)}
          </Typography>
        </Box>
        <Box className={classes.billingItem}>
          <Typography className={classes.billingLabel}>Current:</Typography>
          <Typography className={classes.billingValue}>
            {formatCurrency(billing.currentMonthCost, billing.currency)}
          </Typography>
        </Box>
        <Box className={classes.billingItem}>
          <Typography className={classes.billingLabel}>Projected:</Typography>
          <Typography
            className={`${classes.billingValue} ${
              isHighAlert ? classes.billingHighAlert : isIncrease ? classes.billingIncrease : isDecrease ? classes.billingDecrease : ''
            }`}
          >
            {isIncrease && <span className={classes.billingArrow}>↑</span>}
            {isDecrease && <span className={classes.billingArrow}>↓</span>}
            {formatCurrency(billing.projectedMonthCost, billing.currency)}
            {percentChange !== 0 && billing.previousMonthCost > 0 && (
              <span style={{ fontSize: '0.7rem', marginLeft: 4 }}>
                ({percentChange > 0 ? '+' : ''}{percentChange.toFixed(0)}%)
              </span>
            )}
          </Typography>
        </Box>
        {billing.billingReportUrl && (
          <Tooltip title="View detailed billing report">
            <Link
              className={classes.billingLink}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                window.open(billing.billingReportUrl, '_blank', 'noopener,noreferrer');
              }}
            >
              <OpenInNewIcon style={{ fontSize: 12 }} />
              Details
            </Link>
          </Tooltip>
        )}
      </Box>
    );
  };

  // Render labels as chips
  const renderLabels = () => {
    if (!project.labels || Object.keys(project.labels).length === 0) return null;

    return (
      <Box className={classes.labelsContainer}>
        {Object.entries(project.labels).slice(0, 5).map(([key, value]) => (
          <Chip
            key={key}
            label={`${key}: ${value}`}
            size="small"
            variant="outlined"
            className={classes.labelChip}
          />
        ))}
        {Object.keys(project.labels).length > 5 && (
          <Chip
            label={`+${Object.keys(project.labels).length - 5} more`}
            size="small"
            variant="outlined"
            className={classes.labelChip}
          />
        )}
      </Box>
    );
  };

  // Render catalog panel
  const renderCatalogPanel = () => {
    if (!expanded) return null;

    if (catalogState.loading) {
      return (
        <Box className={classes.catalogPanel}>
          <Box className={classes.loadingIndicator}>
            <CircularProgress size={20} className={classes.loadingSpinner} />
            <Typography variant="body2">Loading catalog information...</Typography>
          </Box>
        </Box>
      );
    }

    if (catalogState.error) {
      return (
        <Box className={classes.catalogPanel}>
          <Alert severity="error">
            Failed to load catalog information: {catalogState.error}
            <Button size="small" onClick={fetchCatalogData}>Retry</Button>
          </Alert>
        </Box>
      );
    }

    if (catalogState.notFound) {
      return (
        <Box className={classes.catalogPanel}>
          <Box className={classes.noCatalogMatch}>
            <Typography variant="body2" color="textSecondary">
              No catalog entity found for this project.
            </Typography>
            <Link
              href={`${appBaseUrl}/catalog?filters[kind]=component&filters[metadata.name]=${project.projectId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Search in Catalog
            </Link>
          </Box>
        </Box>
      );
    }

    const catalogInfo = catalogState.data;
    if (!catalogInfo) return null;

    const hasContent = catalogInfo.parentEntities.length > 0 ||
      catalogInfo.dependencyEntities.length > 0 ||
      catalogInfo.links.length > 0 ||
      catalogInfo.techdocsUrl;

    if (!hasContent) {
      return (
        <Box className={classes.catalogPanel}>
          <Typography variant="body2" color="textSecondary">
            This project exists in the catalog but has no linked resources.
          </Typography>
          <Link
            href={getCatalogEntityUrl(catalogInfo.entity)}
            target="_blank"
            rel="noopener noreferrer"
          >
            View in Catalog
          </Link>
        </Box>
      );
    }

    return (
      <Box className={classes.catalogPanel}>
        {catalogInfo.parentEntities.length > 0 && (
          <Box className={classes.catalogSection}>
            <Typography variant="subtitle2" className={classes.catalogSectionTitle}>
              <SystemIcon fontSize="small" />
              Parent Components
            </Typography>
            <List dense disablePadding>
              {catalogInfo.parentEntities.map(parent => (
                <ListItem
                  key={parent.metadata.name}
                  button
                  className={classes.catalogItem}
                  onClick={() => window.open(getCatalogEntityUrl(parent), '_blank')}
                >
                  <ListItemIcon>
                    <SystemIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.catalogItemText}
                    primary={parent.metadata.title || parent.metadata.name}
                    secondary={`${parent.kind}: ${parent.spec?.type || 'unknown'}`}
                  />
                  <OpenInNewIcon fontSize="small" color="action" />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {catalogInfo.dependencyEntities.length > 0 && (
          <Box className={classes.catalogSection}>
            <Typography variant="subtitle2" className={classes.catalogSectionTitle}>
              <ResourceIcon fontSize="small" />
              Linked Resources ({catalogInfo.dependencyEntities.length})
            </Typography>
            <List dense disablePadding>
              {catalogInfo.dependencyEntities.slice(0, 5).map(dep => (
                <ListItem
                  key={dep.metadata.name}
                  button
                  className={classes.catalogItem}
                  onClick={() => window.open(getCatalogEntityUrl(dep), '_blank')}
                >
                  <ListItemIcon>
                    <ResourceIcon fontSize="small" color="action" />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.catalogItemText}
                    primary={dep.metadata.title || dep.metadata.name}
                    secondary={`${dep.kind}: ${(dep.spec as Record<string, unknown>)?.type || 'unknown'}`}
                  />
                  <OpenInNewIcon fontSize="small" color="action" />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {catalogInfo.links.length > 0 && (
          <Box className={classes.catalogSection}>
            <Typography variant="subtitle2" className={classes.catalogSectionTitle}>
              <LinkIcon fontSize="small" />
              External Links ({catalogInfo.links.length})
            </Typography>
            <Box className={classes.linksContainer}>
              {catalogInfo.links.map((link, idx) => (
                <Chip
                  key={idx}
                  label={link.title || link.url}
                  size="small"
                  clickable
                  onClick={() => window.open(link.url, '_blank')}
                  className={classes.externalLinkChip}
                  icon={<OpenInNewIcon fontSize="small" />}
                />
              ))}
            </Box>
          </Box>
        )}

        {catalogInfo.techdocsUrl && (
          <Box className={classes.catalogSection}>
            <Divider style={{ marginBottom: 8 }} />
            <Button
              variant="outlined"
              size="small"
              startIcon={<DocsIcon />}
              onClick={() => window.open(catalogInfo.techdocsUrl!, '_blank')}
              className={classes.techdocsButton}
            >
              View TechDocs
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
      <ListItem className={classes.listItem}>
        <Box className={classes.projectRow}>
          <Box
            className={classes.projectMain}
            onClick={toggleExpanded}
          >
            <ListItemIcon>
              <ProjectIcon color="primary" />
              {expanded ? (
                <ExpandLessIcon fontSize="small" style={{ marginLeft: -8 }} />
              ) : (
                <ExpandMoreIcon fontSize="small" style={{ marginLeft: -8 }} />
              )}
            </ListItemIcon>
            <Box className={classes.projectContent}>
              <Box className={classes.projectHeader}>
                <Typography variant="body1">
                  {project.displayName || project.projectId}
                </Typography>
                {project.state && project.state !== 'ACTIVE' && (
                  <Chip label={project.state} size="small" className={classes.chip} color="secondary" />
                )}
              </Box>
              <Typography variant="caption" color="textSecondary">
                {project.projectId}
              </Typography>
              {renderBillingInfo()}
              {renderLabels()}
              {/* Show group chips for saved projects (not in org view) */}
              {!isOrgView && project.groups.length > 0 && (
                <div className={classes.groupChips}>
                  {project.groups
                    .filter(g => userGroups.includes(g) || g.startsWith('user:'))
                    .map(groupRef => (
                      <Chip
                        key={groupRef}
                        label={formatGroupName(groupRef)}
                        size="small"
                        className={classes.groupChip}
                        variant="outlined"
                      />
                    ))}
                </div>
              )}
            </Box>
          </Box>
          <Box className={classes.projectActions}>
            <Tooltip title="Open in GCP Console">
              <Link
                component="button"
                onClick={openGcpConsole}
                className={`${classes.actionLink} ${classes.consoleLink}`}
              >
                <GcpIcon size={14} />
                Console
              </Link>
            </Tooltip>
            <Tooltip title={catalogExists.exists ? 'View in Catalog' : (catalogExists.checked ? 'No catalog entry' : 'View in Catalog')}>
              <Link
                href={getCatalogUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className={`${classes.actionLink} ${
                  catalogExists.checked && !catalogExists.exists
                    ? classes.catalogLinkInactive
                    : classes.catalogLink
                }`}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                <BackstageIcon size={14} />
                Catalog
                <OpenInNewIcon fontSize="small" style={{ marginLeft: 2, width: 12, height: 12 }} />
              </Link>
            </Tooltip>
            {editMode && (
              <>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuAnchor(e.currentTarget);
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={() => setMenuAnchor(null)}
                >
                  {onEditGroups && (
                    <MenuItem onClick={() => {
                      setMenuAnchor(null);
                      onEditGroups(project);
                    }}>
                      <EditIcon fontSize="small" style={{ marginRight: 8 }} />
                      Edit Groups
                    </MenuItem>
                  )}
                  {onHide && (
                    <MenuItem onClick={() => {
                      setMenuAnchor(null);
                      onHide(project);
                    }}>
                      <HideIcon fontSize="small" style={{ marginRight: 8 }} />
                      Hide
                    </MenuItem>
                  )}
                  {onDelete && (
                    <MenuItem onClick={() => {
                      setMenuAnchor(null);
                      setConfirmDeleteOpen(true);
                    }}>
                      <DeleteIcon fontSize="small" style={{ marginRight: 8 }} />
                      Delete
                    </MenuItem>
                  )}
                </Menu>
              </>
            )}
          </Box>
        </Box>
        <Collapse in={expanded} timeout="auto" unmountOnExit style={{ width: '100%' }}>
          {renderCatalogPanel()}
        </Collapse>
      </ListItem>

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
      >
        <DialogTitle>Delete GCP Project?</DialogTitle>
        <DialogContent>
          <div className={classes.confirmWarning}>
            <WarningIcon />
            <Typography>This action cannot be undone.</Typography>
          </div>
          <Typography variant="body2" color="textSecondary">
            Are you sure you want to remove "{project.displayName || project.projectId}" from your saved projects?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            className={classes.deleteButton}
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
